// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { scaleLinear, scaleTime } from "d3-scale";
import React, { useCallback, useEffect, useState } from "react";
import { ResponsiveXYFrame } from "semiotic";
import styled, { css, useTheme } from "styled-components";

import useIsMobile from "~utils/react/useIsMobile";

import { ChartLegend } from "../ChartLegend";
import {
  ScrollLayout,
  ScrollWrapper,
  StickyAxis,
  useChartScroll,
} from "../chartScrollUtils";
import { PathwaysTheme } from "../PathwaysTheme";
import VizPathways from "../VizPathways";
import {
  ChartPoint,
  formatMonthAndYear,
  getTickValues,
  placeLinesInSequence,
  PlotLine,
  resolveTimeSeriesLines,
  TimeSeriesLine,
} from "./helpers";
import PopulationTimeSeriesTooltip from "./PopulationTimeSeriesTooltip";

const ChartWrapper = styled(VizPathways)<{
  $tooltipSuppressed: boolean;
}>`
  .PopulationTimeSeriesChart__projectedLine {
    stroke: ${({ theme }) => theme.palette.data.indigo1};
    stroke-width: 2;
    stroke-dasharray: 2 3;
  }

  .projection-area-label {
    fill: ${({ theme }) => theme.palette.slate80};
  }

  .projection-area {
    fill: ${({ theme }) => theme.palette.marble4};
    fill-opacity: 0;
  }

  ${({ $tooltipSuppressed }) =>
    $tooltipSuppressed &&
    css`
      .annotation-layer {
        display: none;
      }
    `}
`;

type Props = {
  title: string;
  subtitle?: string;
  historicalPopulation: ChartPoint[];
  projectedPopulation?: ChartPoint[];
  series?: TimeSeriesLine[];
  chartTop: number;
  chartBottom: number;
  dateSpacing: number;
  beginDate: Date;
  endDate: Date;
  /**
   * Places points one slot apart in date order, rather than by date, so dates
   * the data skips take no space on the x axis.
   */
  placePointsInSequence?: boolean;
  [k: string]: unknown;
};

const SCROLL_THRESHOLD = 20;
const MIN_POINT_WIDTH = 40;
const SCROLL_LEFT_MARGIN = 40;
const MARGIN_RIGHT = 50;
const CHART_HEIGHT = 558;
const MARGIN_TOP = 56;
const MARGIN_BOTTOM = 96;

function computeYTicks(bottom: number, top: number): number[] {
  const ticks: number[] = [];
  const step = Math.ceil((top - bottom) / 5);
  if (step <= 0) return [bottom];
  for (let v = bottom; v <= top; v += step) {
    ticks.push(v);
  }
  return ticks;
}

const PopulationTimeSeriesBaseChart: React.FC<Props> = ({
  title,
  subtitle,
  historicalPopulation,
  projectedPopulation,
  series,
  chartTop,
  chartBottom,
  dateSpacing,
  beginDate,
  endDate,
  placePointsInSequence = false,
  ...chartProps
}) => {
  const theme = useTheme() as PathwaysTheme;
  const { isMobile } = useIsMobile(true);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);

  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setTooltipSuppressed(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [handleEsc]);

  const charWidth = theme.chart.axisLabel.charWidth;
  const leftMargin = (chartTop.toString().length + 1.5) * charWidth;

  const { lines, allPoints } = resolveTimeSeriesLines({
    series,
    historicalPopulation,
    projectedPopulation,
  });

  // Only a chart whose lines are both named and colored can label them.
  const legendItems = lines.flatMap(({ name, color }) =>
    name && color ? [{ name, color }] : [],
  );

  const sequence = placePointsInSequence
    ? placeLinesInSequence(lines, dateSpacing)
    : undefined;
  const bottomAxis = sequence
    ? { tickValues: sequence.tickValues, tickFormat: sequence.formatTick }
    : {
        tickValues: getTickValues(allPoints, dateSpacing),
        tickFormat: (d: Date) => formatMonthAndYear(d),
      };

  const needsScroll = isMobile && allPoints.length > SCROLL_THRESHOLD;
  const scrollWidth = needsScroll
    ? allPoints.length * MIN_POINT_WIDTH + SCROLL_LEFT_MARGIN + MARGIN_RIGHT
    : undefined;

  const { scrollRef, wrapperRef, scrollOffset, fadeRight, fadeLeft } =
    useChartScroll({
      needsScroll,
      scrollWidth,
      itemCount: allPoints.length,
      leftMargin: SCROLL_LEFT_MARGIN,
      rightMargin: MARGIN_RIGHT,
    });

  const plotHeight = CHART_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;

  const yTickValues = computeYTicks(chartBottom, chartTop);

  const frameProps = {
    responsiveWidth: true,
    summaryDataAccessor: "data",
    summaryClass: "projection-area",
    hoverAnnotation: true,
    // eslint-disable-next-line react/no-unstable-nested-components
    tooltipContent: (d: ChartPoint) => <PopulationTimeSeriesTooltip d={d} />,
    lines: sequence?.lines ?? lines,
    lineDataAccessor: "data",
    lineClass: (l: PlotLine) => l.class,
    // Left off entirely unless a line carries its own color, so that the
    // single-population charts keep drawing from their CSS as before.
    ...(legendItems.length
      ? {
          lineStyle: (l: PlotLine) => ({
            stroke: l.color,
            strokeWidth: 2.5,
            fill: "none",
          }),
          // Overrides the dot fill that `pointClass` sets in CSS, so a point
          // matches its own line rather than the single-population color.
          pointStyle: (point: ChartPoint) => ({ fill: point.color }),
        }
      : {}),
    xScaleType: sequence ? scaleLinear() : scaleTime(),
    xAccessor: sequence ? "position" : "date",
    yAccessor: "value",
    size: [558, CHART_HEIGHT],
    margin: {
      left: leftMargin,
      bottom: MARGIN_BOTTOM,
      right: MARGIN_RIGHT,
      top: MARGIN_TOP,
    },
    xExtent: sequence?.xExtent ?? [beginDate, endDate],
    yExtent: [chartBottom, chartTop],
    showLinePoints: true,
    pointClass: "VizPathways__point",
    axes: [
      { orient: "left", tickFormat: (n: number) => n.toLocaleString() },
      { orient: "bottom", ...bottomAxis },
    ],
    ...chartProps,
  };

  const scrollFrameProps = needsScroll
    ? {
        ...frameProps,
        margin: {
          left: SCROLL_LEFT_MARGIN,
          bottom: MARGIN_BOTTOM,
          right: MARGIN_RIGHT,
          top: MARGIN_TOP,
        },
        axes: [
          {
            orient: "left" as const,
            tickFormat: () => "",
            tickValues: yTickValues,
          },
          { orient: "bottom" as const, ...bottomAxis },
        ],
      }
    : undefined;

  return (
    <ChartWrapper
      title={title}
      subtitle={subtitle}
      legend={
        legendItems.length > 1 ? <ChartLegend items={legendItems} /> : undefined
      }
      $tooltipSuppressed={tooltipSuppressed}
    >
      <div
        ref={wrapperRef}
        onMouseMove={
          tooltipSuppressed ? () => setTooltipSuppressed(false) : undefined
        }
      >
        {needsScroll ? (
          <ScrollLayout>
            <StickyAxis width={leftMargin} height={CHART_HEIGHT}>
              {yTickValues.map((tick: number) => {
                const y =
                  MARGIN_TOP +
                  (1 - (tick - chartBottom) / (chartTop - chartBottom)) *
                    plotHeight;
                return (
                  <text
                    key={tick}
                    x={leftMargin - 8}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                  >
                    {tick.toLocaleString()}
                  </text>
                );
              })}
            </StickyAxis>
            <ScrollWrapper
              ref={scrollRef}
              $fadeRight={fadeRight}
              $fadeLeft={fadeLeft}
            >
              <div
                style={{
                  width: scrollWidth,
                  flexShrink: 0,
                  transform: `translateX(-${scrollOffset}px)`,
                }}
              >
                {/* @ts-expect-error semiotic types don't accept Date[] tickValues */}
                <ResponsiveXYFrame {...scrollFrameProps} />
              </div>
            </ScrollWrapper>
          </ScrollLayout>
        ) : (
          // @ts-expect-error semiotic types don't accept Date[] tickValues
          <ResponsiveXYFrame {...frameProps} />
        )}
      </div>
      <div id="chart-description" className="sr-only">
        A line chart showing the population over time.
      </div>
      <div id="chart-instructions" className="sr-only">
        Press tab to access the line, and tab again to access the points. Use
        arrow keys to hear more information about each point.
      </div>
    </ChartWrapper>
  );
};

export default PopulationTimeSeriesBaseChart;
